import Constants from 'expo-constants';
import { usePathname } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { trpc } from '../lib/trpc';
import { trackSupportModalOpened } from '../lib/product-events';
import { supabase } from '../lib/supabase';
import { getLegalUrl, openExternalUrl } from '../lib/utils';
import { Button } from './Button';
import { FormErrorBanner } from './FormErrorBanner';
import { useInAppBanner } from './InAppBanner';
import { ResponsiveModal } from './ResponsiveModal';
import { TextInput } from './TextInput';

const PRIVACY_URL = getLegalUrl('privacy');
const SECURITY_URL = getLegalUrl('security');
const TERMS_URL = getLegalUrl('terms');

const SUPPORT_TOPICS = [
  'Billing or access',
  'Bank connection issue',
  'Bug report',
  'FIRE plan question',
  'General question / feedback',
] as const;

const FAQS = [
  {
    question: 'How do I connect my accounts?',
    answer:
      'Go to Balance Sheet and choose the institution you want to connect. If a provider says your institution needs attention, send a note here and include the institution name.',
  },
  {
    question: 'How is my FIRE projection calculated?',
    answer:
      'Per Diem blends current balances, spending, income, taxes, and scenario inputs to model retirement readiness over time.',
  },
] as const;

interface HelpSupportModalProps {
  visible: boolean;
  onClose: () => void;
}

export function HelpSupportModal({ visible, onClose }: HelpSupportModalProps) {
  const pathname = usePathname();
  const { showBanner } = useInAppBanner();
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: visible });
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: visible });
  const subscriptionQ = trpc.subscription.getStatus.useQuery(undefined, { enabled: visible });
  const submitMutation = trpc.support.submit.useMutation({
    onSuccess: () => {
      setSubject('');
      setMessage('');
      setError(null);
      onClose();
      showBanner({
        kind: 'success',
        title: 'Message sent',
        message: 'Thanks. You should hear back within 24 hours on business days.',
      });
    },
    onError: (err) => {
      setError(err.message || 'Could not send your message. Please try again.');
    },
  });

  const currentProfile = profilesQ.data?.find((profile) => profile.isSelf) ?? profilesQ.data?.[0];
  const inferredName = useMemo(
    () => [currentProfile?.firstName, currentProfile?.lastName].filter(Boolean).join(' ').trim(),
    [currentProfile?.firstName, currentProfile?.lastName],
  );
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [supabaseUserId, setSupabaseUserId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const trackedOpenRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      trackedOpenRef.current = false;
      return;
    }

    if (trackedOpenRef.current) return;
    trackedOpenRef.current = true;
    trackSupportModalOpened({ route: pathname ?? null });
  }, [pathname, visible]);

  useEffect(() => {
    if (!visible) return;

    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const user = data.session?.user;
      setSessionEmail(user?.email ?? null);
      setSupabaseUserId(user?.id ?? null);
      setEmail(user?.email ?? '');
    });

    return () => {
      cancelled = true;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !inferredName) return;
    setName((current) => current || inferredName);
  }, [inferredName, visible]);

  const context = useMemo(
    () => ({
      platform: Platform.OS,
      appVersion:
        Constants.expoConfig?.version
        ?? Constants.nativeAppVersion
        ?? 'unknown',
      ...(pathname ? { route: pathname } : {}),
      ...(sessionEmail ? { accountEmail: sessionEmail } : {}),
      ...(supabaseUserId ? { supabaseUserId } : {}),
      ...(householdQ.data?.id ? { householdId: householdQ.data.id } : {}),
      ...(currentProfile?.id ? { personId: currentProfile.id } : {}),
      ...(typeof householdQ.data?.isDemo === 'boolean' ? { isDemo: householdQ.data.isDemo } : {}),
      ...(subscriptionQ.data?.accessKind ? { subscriptionAccessKind: subscriptionQ.data.accessKind } : {}),
      ...(typeof subscriptionQ.data?.isActive === 'boolean'
        ? { subscriptionIsActive: subscriptionQ.data.isActive }
        : {}),
    }),
    [
      currentProfile?.id,
      householdQ.data?.id,
      householdQ.data?.isDemo,
      pathname,
      sessionEmail,
      subscriptionQ.data?.accessKind,
      subscriptionQ.data?.isActive,
      supabaseUserId,
    ],
  );

  const canSubmit =
    name.trim().length > 0
    && email.trim().length > 0
    && subject.trim().length > 0
    && message.trim().length > 0;

  const handleTopicPress = (topic: string) => {
    setSubject(topic);
    setError(null);
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      setError('Please fill out each field before sending.');
      return;
    }

    setError(null);
    submitMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
      context,
    });
  };

  return (
    <ResponsiveModal
      visible={visible}
      onClose={onClose}
      maxWidth={820}
      maxHeight={780}
      androidMinHeight={560}
      header={
        <Text className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Help & Support
        </Text>
      }
      subtitle={
        <Text className="text-sm leading-5 text-gray-500 dark:text-gray-400">
          Send a note directly to Per Diem support.
        </Text>
      }
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 24, paddingTop: 8, gap: 16 }}
      >
        <View className="gap-3">
          <TextInput
            label="Name"
            value={name}
            onChangeText={(value) => {
              setName(value);
              setError(null);
            }}
            autoCapitalize="words"
            textContentType="name"
          />
          <TextInput
            label="Email"
            value={email}
            onChangeText={(value) => {
              setEmail(value);
              setError(null);
            }}
            autoCapitalize="none"
            keyboardType="email-address"
            textContentType="emailAddress"
            editable={false}
          />
          <View className="gap-2">
            <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Topic
            </Text>
            <View className="flex-row flex-wrap gap-2">
              {SUPPORT_TOPICS.map((topic) => {
                const selected = subject === topic;
                return (
                  <Pressable
                    key={topic}
                    onPress={() => handleTopicPress(topic)}
                    className={`rounded-full border px-3 py-2 ${
                      selected
                        ? 'border-forest-600 bg-green-50 dark:border-forest-500 dark:bg-green-950/40'
                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                    }`}
                  >
                    <Text
                      className={`text-xs font-semibold ${
                        selected
                          ? 'text-forest-700 dark:text-forest-500'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {topic}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <TextInput
            label="Subject"
            value={subject}
            onChangeText={(value) => {
              setSubject(value);
              setError(null);
            }}
            placeholder="What can we help with?"
          />
          <TextInput
            label="Message"
            value={message}
            onChangeText={(value) => {
              setMessage(value);
              setError(null);
            }}
            placeholder="What happened, and what did you expect instead?"
            multiline
            numberOfLines={6}
            textAlignVertical="top"
            inputClassName="min-h-[150px]"
          />
        </View>

        <FormErrorBanner error={error} onDismiss={() => setError(null)} />

        <Button onPress={handleSubmit} loading={submitMutation.isPending} disabled={!canSubmit}>
          Send message
        </Button>

        <View className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <Text className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Common questions
          </Text>
          <View className="mt-2 gap-1">
            {FAQS.map((item, index) => {
              const expanded = expandedIndex === index;
              return (
                <View
                  key={item.question}
                  className="border-b border-gray-200 py-2 last:border-b-0 dark:border-gray-700"
                >
                  <Pressable
                    onPress={() => setExpandedIndex(expanded ? null : index)}
                    className="flex-row items-center justify-between gap-3"
                  >
                    <Text className="flex-1 text-sm font-semibold text-gray-800 dark:text-gray-100">
                      {item.question}
                    </Text>
                    <Ionicons
                      name={expanded ? 'chevron-up' : 'chevron-down'}
                      size={15}
                      color="#6b7280"
                    />
                  </Pressable>
                  {expanded ? (
                    <Text className="mt-2 text-sm leading-5 text-gray-500 dark:text-gray-400">
                      {item.answer}
                    </Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        </View>

        <View className="flex-row flex-wrap gap-3">
          <PolicyLink label="Privacy" onPress={() => openExternalUrl(PRIVACY_URL)} />
          <PolicyLink label="Security" onPress={() => openExternalUrl(SECURITY_URL)} />
          <PolicyLink label="Terms" onPress={() => openExternalUrl(TERMS_URL)} />
        </View>
      </ScrollView>
    </ResponsiveModal>
  );
}

function PolicyLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} className="rounded-full bg-gray-100 px-3 py-2 dark:bg-gray-800">
      <Text className="text-xs font-semibold text-forest-700 dark:text-forest-500">
        {label}
      </Text>
    </Pressable>
  );
}
